/**
 * Entitlement reads and purchase flows.
 *
 * Buying something crosses multiple domains: it flips an ENTITLEMENT, can seed the
 * LICENSE activity log (the Commercial Toolkit), and always toasts. That coordination lives here rather than inside any
 * one store — the stores never reach into each other.
 *
 * ============================================================================
 * NOT A SECURITY BOUNDARY, AND NOTHING IS CHARGED. Every flag is client-side and
 * editable from devtools, and `purchase()` takes no money. Phase 4 makes
 * entitlements server-authoritative; see src/api/productsApi.ts.
 * ============================================================================
 */
import { COMMERCIAL_ADDON } from '../data/products';
import { PRODUCT_ENTITLEMENT } from '../api/productsApi';
import { useBonusClaimsStore } from '../stores/bonusClaimsStore';
import { useCreditsStore } from '../stores/creditsStore';
import { useEntitlementsStore } from '../stores/entitlementsStore';
import { useLicenseStore } from '../stores/licenseStore';
import { usePublisherCreditsStore } from '../stores/publisherCreditsStore';
import { useUiStore } from '../stores/uiStore';
import type { Entitlement, ProductId } from '../types/domain';

/**
 * The date stamped on a Commercial License & Creator Toolkits purchase.
 *
 * The prototype hardcoded "Jul 30, 2026", which was already the wrong answer for
 * any other day. Formatted from the clock instead, in the same style.
 */
function purchaseDateLabel(): string {
  return new Date().toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * What the user is told when a purchase does not complete.
 *
 * Exported so a test can assert the failure is surfaced rather than swallowed.
 */
export const PURCHASE_FAILED_MESSAGE = 'Purchase could not be completed — please try again';

/**
 * What the user is told when checkout went through but the entitlement is not active yet.
 *
 * ============================================================================
 * THE STATE THE APP COULD NOT PREVIOUSLY DESCRIBE. `purchase()` toasted "X unlocked" as soon
 * as checkout resolved, because the store granted the flag in the same breath. It no longer
 * does — the authoritative `GET /entitlements` decides (see entitlementsStore.purchase) — and
 * a real deployment fulfils by webhook, so "paid" and "unlocked" are two events with a gap
 * between them.
 *
 * "Unlocked" for a flag that is still off would be a false claim of a completed grant, which
 * is precisely what PART 12 forbids. So the toast is chosen by looking at what the authority
 * actually says afterwards, and this is the sentence for the gap: pending, not failed, and
 * not done.
 * ============================================================================
 */
export const PURCHASE_PENDING_MESSAGE =
  'Payment received — your purchase is being processed and will unlock shortly';

export function useEntitlements() {
  // One field per selector: Zustand v5 compares with Object.is, so an object
  // literal would re-render every consumer on any store change.
  const commercial = useEntitlementsStore((s) => s.commercial);
  const studio = useEntitlementsStore((s) => s.studio);
  const composerPlus = useEntitlementsStore((s) => s.composerPlus);
  const professional = useEntitlementsStore((s) => s.professional);
  const unlimited = useEntitlementsStore((s) => s.unlimited);
  const dfyVault = useEntitlementsStore((s) => s.dfyVault);
  const reseller = useEntitlementsStore((s) => s.reseller);
  const publisher = useEntitlementsStore((s) => s.publisher);
  const commercialPurchaseDate = useEntitlementsStore((s) => s.commercialPurchaseDate);
  const claimedBonuses = useBonusClaimsStore((s) => s.claimedBonuses);

  const purchaseRaw = useEntitlementsStore((s) => s.purchase);
  const purchaseCommercialRaw = useEntitlementsStore((s) => s.purchaseCommercial);
  const claimBonusRaw = useEntitlementsStore((s) => s.claimBonus);
  const loadBonusClaims = useBonusClaimsStore((s) => s.loadClaims);

  const notePurchase = useLicenseStore((s) => s.notePurchase);
  const showToast = useUiStore((s) => s.showToast);

  /**
   * Buys the Commercial License & Creator Toolkits and activates the Commercial License.
   *
   * The Toolkits are the PRODUCT the user buys; the Commercial License is an
   * ENTITLEMENT included with it. There is nothing to purchase called "Commercial
   * License" on its own — unlocking one is a consequence of owning the other, which is why
   * this single call flips the entitlement, stamps the date, and seeds the licence
   * activity log together.
   *
   * Declared here as a local function rather than as a method on the returned
   * object, because the returned object's `purchase()` needs to call it. An earlier
   * version used `this.purchaseCommercial()`, which threw for every consumer:
   * Products.tsx does `const { purchase } = useEntitlements()`, and destructuring
   * detaches the method from its object, so `this` was `undefined` under ES module
   * strict mode. Buying the Toolkit failed silently and the licence never unlocked.
   * A local function has no receiver to lose.
   */
  async function purchaseCommercialToolkit(): Promise<void> {
    const date = purchaseDateLabel();
    await purchaseCommercialRaw(date);
    /*
      The licence activity log and the toast both follow the AUTHORITY, not the checkout.
      "Your Commercial License is now active" is the strongest claim any toast in this app
      makes — it is what the user relies on to know they may sell what they make — so it is
      the last one that should be printed on the strength of a request having been accepted.
      An entry in the activity log for a licence that is not active would be worse still:
      that log is the record the licence page shows.
    */
    if (!useEntitlementsStore.getState().commercial) {
      showToast(PURCHASE_PENDING_MESSAGE);
      return;
    }
    notePurchase(date);
    showToast(`${COMMERCIAL_ADDON.name} unlocked — your Commercial License is now active`);
  }

  /**
   * Every entitlement in one object, for the Products grid's owned/not-owned map.
   *
   * Rebuilt each render, so it must not be used as a selector result — it is a
   * derived view for rendering only.
   */
  const entitlements: Entitlement = {
    commercial,
    studio,
    composerPlus,
    professional,
    unlimited,
    dfyVault,
    reseller,
    publisher,
    commercialPurchaseDate,
  };

  return {
    ...entitlements,
    claimedBonuses,
    /**
     * Owned/not-owned keyed by purchasable product id, so the Products grid can
     * look a row up directly. `studio`, `composerPlus` and `reseller` are included
     * even though nothing gates on them yet — Phase 4 uses all three.
     */
    ownsProduct: {
      commercial,
      studio,
      composer: composerPlus,
      professional,
      unlimited,
      vault: dfyVault,
      reseller,
      publisher,
    } satisfies Record<ProductId, boolean>,

    /**
     * Buys any one-time product.
     *
     * `commercial` is the Commercial License & Creator Toolkits and routes through the local
     * purchaseCommercialToolkit(), which also activates the Commercial License.
     */
    async purchase(productId: ProductId): Promise<void> {
      if (productId === 'commercial') {
        await purchaseCommercialToolkit();
        return;
      }
      const product = await purchaseRaw(productId);
      /*
        READ AFTER the store has consulted the authority, and through `getState()` rather than
        the `entitlements` object above — that one is this render's snapshot, captured before
        the purchase, so it would still say `false` however the grant went.

        This is the difference between reporting an outcome and asserting one.
      */
      const owned = useEntitlementsStore.getState()[PRODUCT_ENTITLEMENT[productId]];
      showToast(owned ? `${product.name} unlocked` : PURCHASE_PENDING_MESSAGE);
    },

    /**
     * Buys the Commercial License & Creator Toolkits and activates the Commercial License.
     *
     * Kept on the returned object for callers that want that product specifically
     * without going through the product-id dispatch. Both paths run the same
     * function, so they cannot drift apart.
     */
    purchaseCommercial: purchaseCommercialToolkit,

    async claimBonus(bonusId: string): Promise<void> {
      const result = await claimBonusRaw(bonusId);
      void loadBonusClaims();
      if (result.creditGrant) {
        const { amount, kind } = result.creditGrant;
        if (result.alreadyClaimed) {
          showToast(`You already claimed ${result.bonusName}`);
          return;
        }
        if (kind === 'publisher') usePublisherCreditsStore.getState().grant(amount);
        else useCreditsStore.getState().grant(amount, `Bonus credit pack: ${result.bonusName}`);
        const label = kind === 'publisher' ? 'Publisher credits' : 'credits';
        showToast(
          `You claimed ${result.bonusName}. ${amount.toLocaleString('en-US')} ${label} have been added.`
        );
        return;
      }
      showToast(
        result.alreadyClaimed
          ? `You already claimed ${result.bonusName}`
          : `You claimed ${result.bonusName}`
      );
    },
  };
}
